/**
 * 円形プログレスリング（Apple Watch風）
 *
 * 歩数、アクティブ時間、カロリーなどの進捗を視覚的に表示
 */

import { cn } from '@/lib/utils'

type Props = {
  value: number
  max: number
  size?: number
  strokeWidth?: number
  color?: string
  bgColor?: string
  label?: string
  unit?: string
  icon?: React.ReactNode
  className?: string
}

export const ProgressRing = ({
  value,
  max,
  size = 120,
  strokeWidth = 10,
  color = 'var(--primary)',
  bgColor = 'var(--muted)',
  label,
  unit,
  icon,
  className,
}: Props) => {
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const percentage = Math.min((value / max) * 100, 100)
  const strokeDashoffset = circumference - (percentage / 100) * circumference

  return (
    <div className={cn('flex flex-col items-center gap-2', className)}>
      <div className="relative" style={{ width: size, height: size }}>
        {/* 背景リング */}
        <svg className="absolute inset-0 -rotate-90" width={size} height={size}>
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={bgColor} strokeWidth={strokeWidth} />
        </svg>

        {/* プログレスリング */}
        <svg className="absolute inset-0 -rotate-90" width={size} height={size}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-500 ease-out"
          />
        </svg>

        {/* 中央コンテンツ */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {icon && <div className="mb-1">{icon}</div>}
          <span className="text-xl font-bold tabular-nums">{value.toLocaleString()}</span>
          {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
        </div>
      </div>

      {label && <span className="text-sm text-muted-foreground">{label}</span>}
    </div>
  )
}
